import React, { useState, useEffect } from 'react';
import { Col, Statistic, Empty, Table, DatePicker, Select, Button, message, Switch } from 'antd';
import {
  ClockCircleOutlined,
  TeamOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  UserDeleteOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import Chart from 'react-apexcharts';
import { usePageHeader } from '../../../layouts/AdminLayout';
import HeartLoader from '../../../components/shared/HeartLoader';
import {
  StyledAnalyticsTabs,
  TabsAndFiltersRow,
  FiltersContainer,
  ChartCard,
  StatCard,
  StatsRow,
  ChartsRow,
  PageContainer,
  selectStyles,
  rangePickerStyles,
  valueColors,
  suffixStyles,
  iconColors
} from '../styled';
import { COLORS } from '../../../constants/colors';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

const Operations = ({ dateRange: propDateRange, timeRange: propTimeRange }) => {
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('throughput');
  const [dateRange, setDateRange] = useState(propDateRange || [dayjs().subtract(30, 'days'), dayjs()]);
  const [quickRange, setQuickRange] = useState(propTimeRange || '30days');
  const timeRange = propTimeRange || 'month';
  
  const [throughputData, setThroughputData] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [noShowData, setNoShowData] = useState(null);
  const [durationData, setDurationData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [excludeArchived, setExcludeArchived] = useState(true);
  const { setPageHeader } = usePageHeader();

  // Set page header
  useEffect(() => {
    setPageHeader({ title: 'Operations Analytics', count: null, countIcon: null });
  }, [setPageHeader]);

  useEffect(() => {
    loadData();
  }, [dateRange, timeRange, excludeArchived]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD')
      };
      const response = await api.getBlob('/screening/exports/operations-data', params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `operations-data-${dateRange[0].format('YYYY-MM-DD')}-${dateRange[1].format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Export downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        timeRange,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        ...(excludeArchived && { excludeArchived: 'true' })
      };

      const [throughputRes, staffRes, noShowRes, durationRes] = await Promise.all([
        api.get('/screening/analytics/operations/throughput', params),
        api.get('/screening/analytics/operations/staff-productivity', params),
        api.get('/screening/analytics/operations/no-show', params),
        api.get('/screening/analytics/operations/durations', params)
      ]);

      const extractData = (res) => res.data?.success !== undefined 
        ? (res.data.success ? res.data.data : null) 
        : res.data;

      if (extractData(throughputRes)) setThroughputData(extractData(throughputRes));
      if (extractData(staffRes)) setStaffData(extractData(staffRes));
      if (extractData(noShowRes)) setNoShowData(extractData(noShowRes));
      if (extractData(durationRes)) setDurationData(extractData(durationRes));
    } catch (error) {
      console.error('Error loading operations data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderThroughput = () => {
    if (!throughputData) return <Empty description="No data available" />;

    // Status donut chart
    const pieOptions = {
      chart: { type: 'donut' },
      labels: throughputData.statusBreakdown?.map(s => s.label) || [],
      colors: throughputData.statusBreakdown?.map(s => s.color) || ['#1890ff', '#108043', '#b91c1c'],
      legend: { position: 'bottom' },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(1)}%`
      }
    };
    const pieSeries = throughputData.statusBreakdown?.map(s => s.count) || [];

    // Peak hours bar chart
    const peakOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: [COLORS.$primaryBlue],
      xaxis: { categories: throughputData.peakHours?.map(h => h.label) || [] },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (val) => `${val} visits` } }
    };
    const peakSeries = [{ name: 'Visits', data: throughputData.peakHours?.map(h => h.count) || [] }];

    // Trend area chart
    const trendOptions = {
      chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { curve: 'smooth', width: 2 },
      colors: ['#1890ff', '#108043'],
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1 } },
      xaxis: { categories: throughputData.throughputTrend?.map(d => dayjs(d.date).format('MMM D')) || [] },
      legend: { position: 'top' },
      tooltip: { shared: true }
    };
    const trendSeries = [
      { name: 'Total', data: throughputData.throughputTrend?.map(d => d.total) || [] },
      { name: 'Completed', data: throughputData.throughputTrend?.map(d => d.completed) || [] }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total Visits"
                value={throughputData.summary.totalVisits}
                prefix={<DashboardOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Completed"
                value={throughputData.summary.completedVisits}
                valueStyle={{ color: valueColors.success }}
                prefix={<CheckCircleOutlined />}
                suffix={<span style={suffixStyles}>{throughputData.summary.completionRate}%</span>}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="No-Shows"
                value={throughputData.summary.noShowCount}
                valueStyle={{ color: valueColors.danger }}
                prefix={<UserDeleteOutlined />}
                suffix={<span style={suffixStyles}>{throughputData.summary.noShowRate}%</span>}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Status Breakdown">
              <Chart options={pieOptions} series={pieSeries} type="donut" height={300} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Peak Hours">
              <Chart options={peakOptions} series={peakSeries} type="bar" height={300} />
            </ChartCard>
          </Col>
        </ChartsRow>

        {throughputData.throughputTrend?.length > 0 && (
          <ChartCard title="Daily Throughput Trend">
            <Chart options={trendOptions} series={trendSeries} type="area" height={300} />
          </ChartCard>
        )}
      </>
    );
  };

  const renderStaffProductivity = () => {
    if (!staffData) return <Empty description="No data available" />;

    const columns = [
      { title: 'Staff Member', dataIndex: 'name', key: 'name' },
      { title: 'Email', dataIndex: 'email', key: 'email' },
      { title: 'Visits Completed', dataIndex: 'visitsCompleted', key: 'visitsCompleted', sorter: (a, b) => a.visitsCompleted - b.visitsCompleted },
      { 
        title: 'Avg Screening Time', 
        dataIndex: 'avgScreeningTime', 
        key: 'avgScreeningTime',
        render: (val) => val ? `${val} min` : 'N/A'
      }
    ];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic
                title="Active Staff"
                value={staffData.summary.totalStaff}
                prefix={<TeamOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic
                title="Total Visits Completed"
                value={staffData.summary.totalVisitsCompleted}
                prefix={<CheckCircleOutlined style={iconColors.success} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard>
              <Statistic
                title="Avg Visits per Staff"
                value={staffData.summary.avgVisitsPerStaff}
                prefix={<DashboardOutlined style={iconColors.purple} />}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartCard title="Staff Productivity">
          <Table
            dataSource={staffData.staffProductivity}
            columns={columns}
            rowKey="staffId"
            pagination={{ pageSize: 10 }}
          />
        </ChartCard>
      </>
    );
  };

  const renderNoShow = () => {
    if (!noShowData) return <Empty description="No data available" />;

    // No-shows by day grouped bar
    const dayOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: ['#b91c1c', '#1890ff'],
      xaxis: { categories: noShowData.noShowByDay?.map(d => d.day) || [] },
      dataLabels: { enabled: false },
      legend: { position: 'top' }
    };
    const daySeries = [
      { name: 'No-Shows', data: noShowData.noShowByDay?.map(d => d.noShows) || [] },
      { name: 'Total Visits', data: noShowData.noShowByDay?.map(d => d.total) || [] }
    ];

    // Cancellation reasons horizontal bar
    const reasonsOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      colors: ['#faad14'],
      xaxis: { categories: noShowData.cancellationReasons?.map(r => r.reason) || [] },
      dataLabels: { enabled: false }
    };
    const reasonsSeries = [{ name: 'Count', data: noShowData.cancellationReasons?.map(r => r.count) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Total No-Shows"
                value={noShowData.summary.totalNoShows}
                valueStyle={{ color: valueColors.danger }}
                prefix={<UserDeleteOutlined />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="No-Show Rate"
                value={noShowData.summary.noShowRate}
                suffix="%"
                valueStyle={{ color: parseFloat(noShowData.summary.noShowRate) > 15 ? '#b91c1c' : '#108043' }}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Cancellations"
                value={noShowData.summary.totalCancellations}
                valueStyle={{ color: valueColors.warning }}
                prefix={<WarningOutlined />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Cancellation Rate"
                value={noShowData.summary.cancellationRate}
                suffix="%"
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="No-Shows by Day of Week">
              <Chart options={dayOptions} series={daySeries} type="bar" height={300} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Cancellation Reasons">
              {noShowData.cancellationReasons?.length > 0 ? (
                <Chart options={reasonsOptions} series={reasonsSeries} type="bar" height={300} />
              ) : (
                <Empty description="No cancellation reasons recorded" />
              )}
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const renderDurations = () => {
    if (!durationData) return <Empty description="No data available" />;

    const stageData = [
      { stage: 'Form to Intake', avg: durationData.stages?.formToIntake?.avg || 0, median: durationData.stages?.formToIntake?.median || 0 },
      { stage: 'Intake to Bus', avg: durationData.stages?.intakeToBus?.avg || 0, median: durationData.stages?.intakeToBus?.median || 0 },
      { stage: 'Screening', avg: durationData.stages?.screening?.avg || 0, median: durationData.stages?.screening?.median || 0 },
      { stage: 'Total Visit', avg: durationData.stages?.total?.avg || 0, median: durationData.stages?.total?.median || 0 }
    ];

    // Duration by stage grouped bar
    const stageOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
      colors: ['#1890ff', '#108043'],
      xaxis: { categories: stageData.map(s => s.stage) },
      dataLabels: { enabled: false },
      legend: { position: 'top' },
      tooltip: { y: { formatter: (val) => `${val} min` } }
    };
    const stageSeries = [
      { name: 'Average', data: stageData.map(s => s.avg) },
      { name: 'Median', data: stageData.map(s => s.median) }
    ];

    // Distribution bar chart
    const distOptions = {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: '60%', distributed: true } },
      colors: ['#722ed1', '#1890ff', '#108043', '#faad14', '#b91c1c'],
      xaxis: { categories: durationData.distribution?.map(d => d.label) || [] },
      dataLabels: { enabled: true, style: { fontSize: '11px' } },
      legend: { show: false },
      tooltip: { y: { formatter: (val) => `${val} visits` } }
    };
    const distSeries = [{ name: 'Visits', data: durationData.distribution?.map(d => d.count) || [] }];

    return (
      <>
        <StatsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Visits with Timing"
                value={durationData.summary.visitsWithTiming}
                prefix={<ClockCircleOutlined style={iconColors.primary} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Coverage Rate"
                value={durationData.summary.coverageRate}
                suffix="%"
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Avg Total Visit"
                value={durationData.stages?.total?.avg || 0}
                suffix=" min"
                prefix={<ClockCircleOutlined style={iconColors.purple} />}
              />
            </StatCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatCard>
              <Statistic
                title="Avg Screening Time"
                value={durationData.stages?.screening?.avg || 0}
                suffix=" min"
                prefix={<ClockCircleOutlined style={iconColors.success} />}
              />
            </StatCard>
          </Col>
        </StatsRow>

        <ChartsRow gutter={24}>
          <Col xs={24} lg={12}>
            <ChartCard title="Duration by Stage (minutes)">
              <Chart options={stageOptions} series={stageSeries} type="bar" height={300} />
            </ChartCard>
          </Col>
          <Col xs={24} lg={12}>
            <ChartCard title="Total Visit Duration Distribution">
              <Chart options={distOptions} series={distSeries} type="bar" height={300} />
            </ChartCard>
          </Col>
        </ChartsRow>
      </>
    );
  };

  const subTabItems = [
    { key: 'throughput', label: 'Queue Throughput' },
    { key: 'staff', label: 'Staff Productivity' },
    { key: 'no-show', label: 'No-Show Analysis' },
    { key: 'durations', label: 'Visit Durations' }
  ];

  const renderContent = () => {
    switch (activeSubTab) {
      case 'staff': return renderStaffProductivity();
      case 'no-show': return renderNoShow();
      case 'durations': return renderDurations();
      default: return renderThroughput();
    }
  };

  const handleDateRangeChange = (dates) => {
    if (dates) {
      setDateRange(dates);
      setQuickRange('custom');
    }
  };

  const handleQuickRangeChange = (value) => {
    setQuickRange(value);
    const now = dayjs();
    let newRange;
    switch (value) {
      case '7days': newRange = [now.subtract(7, 'days'), now]; break;
      case '14days': newRange = [now.subtract(14, 'days'), now]; break;
      case '30days': newRange = [now.subtract(30, 'days'), now]; break;
      case '3months': newRange = [now.subtract(3, 'months'), now]; break;
      case 'Q1': newRange = [dayjs().startOf('year'), dayjs().startOf('year').month(2).endOf('month')]; break;
      case 'Q2': newRange = [dayjs().startOf('year').month(3), dayjs().startOf('year').month(5).endOf('month')]; break;
      case 'Q3': newRange = [dayjs().startOf('year').month(6), dayjs().startOf('year').month(8).endOf('month')]; break;
      case 'Q4': newRange = [dayjs().startOf('year').month(9), dayjs().startOf('year').month(11).endOf('month')]; break;
      default: return;
    }
    setDateRange(newRange);
  };

  if (loading && !throughputData) {
    return (
      <PageContainer>
        <HeartLoader size="48px" fullHeight />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <TabsAndFiltersRow className="filters-animate-in">
        <StyledAnalyticsTabs activeKey={activeSubTab} onChange={setActiveSubTab} items={subTabItems} />
        <FiltersContainer>
          <Select value={quickRange} onChange={handleQuickRangeChange} style={selectStyles} size="large">
            <Option value="7days">Last 7 days</Option>
            <Option value="14days">Last 14 days</Option>
            <Option value="30days">Last 30 days</Option>
            <Option value="3months">Last 3 months</Option>
            <Option value="Q1">Q1 (Jan-Mar)</Option>
            <Option value="Q2">Q2 (Apr-Jun)</Option>
            <Option value="Q3">Q3 (Jul-Sep)</Option>
            <Option value="Q4">Q4 (Oct-Dec)</Option>
            <Option value="custom">Custom</Option>
          </Select>
          <RangePicker value={dateRange} onChange={handleDateRangeChange} format="MM/DD/YY" style={rangePickerStyles} size="large" allowClear={false} />
          <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting} size="large">Export</Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Switch size="small" checked={excludeArchived} onChange={setExcludeArchived} />
            <span style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Exclude Archived</span>
          </div>
        </FiltersContainer>
      </TabsAndFiltersRow>

      {loading ? <HeartLoader size="32px" padding="40px" /> : <div className="content-animate-in">{renderContent()}</div>}
    </PageContainer>
  );
};

export default Operations;
